import type { Credentials, PortalUser } from '@/services/api/types';

/**
 * Persists the session required to restore authentication after a page refresh.
 *
 * SECURITY DECISION:
 *  - The Portal re-validates username + password on every request, so the client
 *    has to hold that data for the lifetime of the session.
 *  - MDenture deliberately uses **sessionStorage** rather than localStorage: the
 *    session survives a refresh but is cleared when the tab closes, which keeps
 *    the exposure window as small as the contract allows.
 *  - This is browser-visible storage, NOT a secure vault. The stored session is
 *    always re-verified against the real Portal API on app start.
 *
 * IFRAME NOTE: MDenture is embedded in the m-denture.zangesa.com host page. Modern
 * browsers partition storage per top-level site, so this session is scoped to the
 * embedding site and is never shared with a directly-opened MDenture tab. If the
 * user has third-party storage entirely blocked, every call below fails silently
 * and the session simply does not survive a reload — the app still works.
 *
 * The value is base64-encoded only to avoid trivially readable plaintext in the
 * storage inspector — this is obfuscation, not encryption, and is documented as such.
 */

const STORAGE_KEY = 'mdenture.auth';

/**
 * A restorable MDenture session.
 *
 * Two shapes are supported:
 *  - `credentials` — the classic email/password session re-sent on every request.
 *  - `jwt`         — a single-absolute-expiry bearer token issued by `SuiteAPI/User/ConfirmEmailV1`
 *                    during the registration flow (120-minute window, no refresh).
 */
export type StoredSession =
  | { type: 'credentials'; email: string; password: string }
  | { type: 'jwt'; token: string; user?: PortalUser };

const storedUserFields = [
  'id',
  'profileID',
  'eMail',
  'title',
  'firstname',
  'lastname',
  'initials',
  'orgID',
  'orgName',
  'orgDisplayName',
  'language',
] as const;

function encode(value: string): string {
  return btoa(unescape(encodeURIComponent(value)));
}

function decode(value: string): string {
  return decodeURIComponent(escape(atob(value)));
}

function readStoredUser(value: unknown): PortalUser | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  const user: PortalUser = {};

  for (const field of storedUserFields) {
    const fieldValue = record[field];
    if (typeof fieldValue === 'string' || fieldValue === null) {
      Object.assign(user, { [field]: fieldValue });
    }
  }

  return Object.keys(user).length > 0 ? user : undefined;
}

export function saveSession(session: StoredSession): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, encode(JSON.stringify(session)));
  } catch {
    // Storage unavailable (private mode / partitioned iframe). The session
    // simply won't survive a refresh; not fatal.
  }
}

export function loadSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(decode(raw)) as Record<string, unknown>;
    if (parsed.type === 'jwt' && typeof parsed.token === 'string') {
      const user = readStoredUser(parsed.user);
      return user ? { type: 'jwt', token: parsed.token, user } : { type: 'jwt', token: parsed.token };
    }
    if (
      parsed.type === 'credentials' &&
      typeof parsed.email === 'string' &&
      typeof parsed.password === 'string'
    ) {
      return { type: 'credentials', email: parsed.email, password: parsed.password };
    }
    return null;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore — nothing else we can do.
  }
}

/** Convenience wrapper: stores an email/password session. */
export function saveCredentials(credentials: Credentials): void {
  saveSession({ type: 'credentials', email: credentials.email, password: credentials.password });
}

/** Convenience wrapper: returns the stored credentials, or null for any other session shape. */
export function loadCredentials(): Credentials | null {
  const session = loadSession();
  return session?.type === 'credentials' ? { email: session.email, password: session.password } : null;
}

/** Convenience wrapper: clears any stored session. */
export function clearCredentials(): void {
  clearSession();
}
